import React from 'react'
import { Avatar, Box, Chip, IconButton, Toolbar, Typography } from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import usePrimaryColor from '../../../lib/hooks/usePrimaryColor'
import ASSISTANT_NAME from '../assistantName'
import { ResizableDrawer } from '../ResizableDrawer'

// Stable id used by AssistantFab's aria-controls so AT can resolve the
// FAB → controlled-region relationship.
export const ASSISTANT_DRAWER_PAPER_ID = 'assistant-drawer-paper'

const ASSISTANT_DRAWER_TITLE_ID = 'assistant-drawer-title'

const CLOSE_LABEL = `Close ${ASSISTANT_NAME}`

export interface AssistantDrawerProps {
  isOpen: boolean
  onClose: () => void
  children?: React.ReactNode
  onClearHistory?: () => void
}

/**
 * Right-anchored drawer for the Civitas Learning AI assistant panel.
 * Header has a light gray background; the avatar uses the institution's primaryColor, the title uses the default text color.
 */
const AssistantDrawer = ({ isOpen, onClose, children, onClearHistory }: AssistantDrawerProps) => {
  const color = usePrimaryColor()

  return (
    <ResizableDrawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      minWidth={500}
      maxWidth={1200}
      defaultWidth={700}
      resizeHandleLabel="Resize assistant panel"
      // aria-modal is set explicitly so the invisible-backdrop ModalProps below
      // does not weaken the modal contract for AT. id wires up AssistantFab's
      // aria-controls so the FAB → drawer relationship is discoverable.
      // aria-labelledby belongs here and not on the drawer itself: MUI spreads
      // root props onto the Modal wrapper, so a name passed there lands on a
      // role-less div and leaves this dialog unnamed.
      PaperProps={{
        id: ASSISTANT_DRAWER_PAPER_ID,
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': ASSISTANT_DRAWER_TITLE_ID,
        sx: { overflow: 'hidden' }
      }}
      ModalProps={{ BackdropProps: { invisible: true } }}
    >
      {/* data-agent-exclude prevents browser AI extensions from processing the chat panel content */}
      <Box data-agent-exclude sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Toolbar
          sx={{
            backgroundColor: 'grey.100',
            borderBottom: 1,
            borderColor: 'grey.300',
            position: 'relative'
          }}
        >
          <Avatar sx={{ width: 32, height: 32, backgroundColor: color }}>
            <AutoAwesomeIcon sx={{ fontSize: 18, color: '#fff' }} />
          </Avatar>
          <Typography
            id={ASSISTANT_DRAWER_TITLE_ID}
            variant="h6"
            component="h2"
            sx={{
              color: 'text.primary',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              fontWeight: 600
            }}
          >
            {ASSISTANT_NAME}
          </Typography>
          <Chip
            label="Beta"
            size="small"
            sx={{
              color: 'text.secondary',
              borderColor: 'grey.400',
              height: 20,
              fontSize: '0.7rem',
              ml: 1
            }}
            variant="outlined"
          />
          {onClearHistory && (
            <IconButton
              onClick={onClearHistory}
              aria-label="Clear history"
              size="small"
              sx={{ color: 'text.secondary', ml: 'auto' }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          )}
          <IconButton
            onClick={onClose}
            aria-label={CLOSE_LABEL}
            edge="end"
            size="small"
            sx={{ color: 'text.secondary', ml: onClearHistory ? 0.5 : 'auto' }}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
        {children}
      </Box>
    </ResizableDrawer>
  )
}

export default AssistantDrawer
